import React from "react";
import { Card, Tag, Typography } from "antd";
import styled from "@emotion/styled";
import {
  normalizeBookingStatus,
  statusColors,
} from "../../utils/bookingStatus";

const { Text } = Typography;

const DiffRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  font-size: 13px;
`;

const DiffSide = styled.div<{ $side: "old" | "new" }>`
  padding: 8px 12px;
  background: ${(p) => (p.$side === "old" ? "#fff2f0" : "#f6ffed")};
  border-right: ${(p) => (p.$side === "old" ? "1px solid #ffccc7" : "none")};
  min-width: 0;
  word-break: break-word;
`;

const DiffHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8c8c8c;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-bottom: 2px;
`;

const EntityBlock = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export type DiffField = {
  label: string;
  oldValue: React.ReactNode;
  newValue: React.ReactNode;
  changed: boolean;
};

type DiffEntity = {
  key: string | number;
  title: React.ReactNode;
  fields: DiffField[];
};

interface BulkUpdateDiffPreviewProps {
  title: string;
  entities: DiffEntity[];
  emptyText?: string;
}

const renderValue = (value: React.ReactNode, changed: boolean, side: "old" | "new") => {
  if (value == null || value === "" || value === "-") {
    return <Text type="secondary">—</Text>;
  }
  if (!changed) {
    return <Text type="secondary">{value}</Text>;
  }
  if (side === "old") {
    return <Text delete type="danger">{value}</Text>;
  }
  return <Text strong style={{ color: "#389e0d" }}>{value}</Text>;
};

export const BulkUpdateDiffPreview: React.FC<BulkUpdateDiffPreviewProps> = ({
  title,
  entities,
  emptyText = "No items to update",
}) => (
  <Card title={title} size="small">
    {entities.length === 0 ? (
      <Text type="secondary">{emptyText}</Text>
    ) : (
      entities.map((entity) => {
        const changedFields = entity.fields.filter((f) => f.changed);
        const fieldsToShow =
          changedFields.length > 0 ? changedFields : entity.fields;

        return (
          <EntityBlock key={entity.key}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{entity.title}</Text>
              {changedFields.length === 0 && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  No changes
                </Text>
              )}
            </div>
            {changedFields.length > 0 && (
              <>
                <DiffHeader>
                  <div>Before</div>
                  <div>After</div>
                </DiffHeader>
                {fieldsToShow.map((field) => (
                  <DiffRow key={field.label}>
                    <DiffSide $side="old">
                      <FieldLabel>{field.label}</FieldLabel>
                      {renderValue(field.oldValue, field.changed, "old")}
                    </DiffSide>
                    <DiffSide $side="new">
                      <FieldLabel>{field.label}</FieldLabel>
                      {renderValue(field.newValue, field.changed, "new")}
                    </DiffSide>
                  </DiffRow>
                ))}
              </>
            )}
          </EntityBlock>
        );
      })
    )}
  </Card>
);

export const bookingStatusTag = (status: string | null | undefined) => {
  if (!status) return "—";
  const normalized = normalizeBookingStatus(status);
  return (
    <Tag
      color={
        statusColors[normalized as keyof typeof statusColors] || "default"
      }
      style={{ margin: 0 }}
    >
      {normalized}
    </Tag>
  );
};
